import { useAuthContext } from "@/context/AuthContext";
import NewsBanner from "@/components/NewsBanner";
import CanceledSubscriptionCard from "@/components/CanceledSubscriptionCard";
import FullTeamDesktop from "./FullTeamDesktop";
import { newsConfig } from "@/lib/newsConfig";
import { SubscriptionStatus } from "@/lib/types";

interface AboutWisWizProps {
  hideWelcomeHeader?: boolean;
  hideNewsBanner?: boolean;
  compact?: boolean;
}

export default function AboutWisWiz({
  hideWelcomeHeader = false,
  hideNewsBanner = false,
  compact = false
}: AboutWisWizProps) {
  const { userData, isParent } = useAuthContext();
  const firstName = userData?.first_name;

  // Check for canceled subscription with remaining access
  const subscription = (userData?.subscriptions ?? [])[0];
  const isCanceledWithAccess =
    subscription?.status === SubscriptionStatus.canceled &&
    !subscription.link_details &&
    subscription.valid_until &&
    new Date(subscription.valid_until) >= new Date();

  return (
    <div className={compact ? "px-3 py-3" : "px-4 py-4"}>
      <div
        className={`${compact ? "max-w-md space-y-5 text-sm" : "max-w-xl space-y-6"} mx-auto`}
      >
        {/* Welcome Header — hidden when HeroPremium renders it centered above */}
        {!hideWelcomeHeader && (
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-6">
              Welkom terug bij <span className="text-mathorange">Wis</span>
              <span className="text-mathyellow">Wiz</span>, {firstName}!
            </h1>
          </div>
        )}

        {/* Canceled subscription banner */}
        {isCanceledWithAccess && subscription.valid_until && (
          <CanceledSubscriptionCard
            validUntil={subscription.valid_until}
            allowParentTakeover={subscription.is_gift !== true}
          />
        )}

        {/* Mission text (when no news is visible) */}
        {(!newsConfig.active || hideNewsBanner) && !isCanceledWithAccess ? (
          <div
            className={`space-y-4 text-gray-700 ${
              compact ? "leading-6" : "leading-relaxed"
            }`}
          >
            <p>
              Nederlandse leerlingen scoren internationaal gezien steeds lager
              in wiskunde. Wij geloven dat we dit kunnen tegengaan door iedere
              leerling de juiste gepersonaliseerde hulp te geven. Om die reden
              hebben we WisWiz gebouwd.
            </p>
          </div>
        ) : null}

        {!hideNewsBanner && <NewsBanner />}

        {/* Community / Kwetsbare Messaging */}
        <div className={compact ? "space-y-3" : "space-y-4"}>
          <h3
            className={`${compact ? "text-base" : "text-lg"} font-bold text-gray-900`}
          >
            WisWiz wordt elke dag beter
          </h3>
          <div
            className={`text-gray-700 ${
              compact ? "space-y-3 leading-6" : "space-y-4 leading-relaxed"
            }`}
          >
            <p>
              We zijn nog maar net begonnen, maar zijn een gedreven team dat
              hard werkt aan verbetering. Je kan ons helpen WisWiz perfect te
              maken door wanneer je iets mist, iets niet goed werkt of je een
              idee hebt dat met ons te delen.
            </p>

            <p className="text-sm">
              Voor alle vragen, feedback en ideeën kan je dus altijd Philip
              whatsappen of bellen op{" "}
              <a
                href="tel:+31630231640"
                className="font-bold text-info hover:text-info/80 transition-colors"
                aria-label="Bel Philip op 06 30 23 16 40"
              >
                06 30 23 16 40
              </a>
              .
            </p>

            {isParent && (
              <p>
                <span className="font-semibold">
                  Bevalt WisWiz? Vertel het dan aan andere ouders in je
                  omgeving.
                </span>{" "}
                Dat zou ons enorm helpen om meer leerlingen te bereiken.
              </p>
            )}
          </div>
        </div>

        {/* Team Section */}
        <div
          className={`${compact ? "mt-6 pt-6" : "mt-8 pt-8"} border-t border-gray-200`}
        >
          <FullTeamDesktop />
        </div>
      </div>
    </div>
  );
}
